import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface ToastChipProps {
  message: string;
  open: boolean;
  onClose: () => void;
  /** Auto-dismiss delay in ms. Default: 3200 */
  duration?: number;
  /**
   * Colour + layout intent. "success" (default) is the sage confirmation pill; "error" is the wine
   * failure pill and lets the message wrap, because a denial sentence is longer than "Saved".
   */
  variant?: "success" | "error";
}

/** Per-variant colours, mirroring the status chips (sage for good, wine for a failure). */
const VARIANT_STYLES = {
  success: { background: "var(--color-good-sage-4)", color: "var(--color-good-sage)" },
  error: { background: "var(--color-good-wine-4)", color: "var(--color-good-wine)" },
} as const;

/**
 * Top-center pill toast — slides down from above the viewport on open,
 * reverses on close. Sage brand colors match the prototype's showUpgradeToast.
 * Rendered via portal so it floats above any z-index stacking context.
 */
export default function ToastChip({
  message,
  open,
  onClose,
  duration = 3200,
  variant = "success",
}: ToastChipProps) {
  // "visible" drives the CSS transition; "mounted" keeps the DOM node alive
  // during the exit animation.
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // setTimeout guarantees the browser paints the off-screen initial state
      // before the enter transition fires (double-rAF is unreliable in React 18).
      const showId = setTimeout(() => setVisible(true), 16);
      timerRef.current = setTimeout(() => {
        setVisible(false);
      }, duration);
      return () => clearTimeout(showId);
    } else {
      setVisible(false);
      if (timerRef.current) clearTimeout(timerRef.current);
    }
  }, [open, duration]);

  // Remove from DOM after exit transition (450ms matches prototype)
  function handleTransitionEnd() {
    if (!visible) {
      setMounted(false);
      onClose();
    }
  }

  if (!mounted) return null;

  return createPortal(
    <div
      onTransitionEnd={handleTransitionEnd}
      style={{
        position: "fixed",
        top: 80,
        left: "50%",
        transform: visible ? "translate(-50%, 0)" : "translate(-50%, -200px)",
        opacity: visible ? 1 : 0,
        background: VARIANT_STYLES[variant].background,
        color: VARIANT_STYLES[variant].color,
        padding: "12px 22px",
        borderRadius: variant === "error" ? 16 : 999,
        fontFamily: "var(--font-lato, sans-serif)",
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: "0.3px",
        boxShadow: "0 6px 18px rgba(0,0,0,0.12)",
        zIndex: 99999,
        pointerEvents: "none",
        // A denial sentence is longer than "Saved" — let the error pill wrap within a sane width.
        whiteSpace: variant === "error" ? "normal" : "nowrap",
        maxWidth: variant === "error" ? "min(90vw, 440px)" : undefined,
        textAlign: variant === "error" ? "center" : undefined,
        transition: visible
          ? "transform 0.4s ease-in-out, opacity 0.35s ease"
          : "transform 0.4s cubic-bezier(0.22, 0.61, 0.36, 1), opacity 0.35s ease",
      }}
    >
      {message}
    </div>,
    document.body,
  );
}
